import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { generateFetchFeedQuery } from '../net/feed-fetch-query.ts';
import { FeedQueryResult } from '../net/softbox-types.ts';
import { useTranslation } from 'react-i18next';

interface UseWidgetDataResult<T> {
  data: T[];
  loading: boolean;
  error: string;
  refetch: () => Promise<void>;
}

/**
 * Custom hook that acts as a "superclass" for all widget functionality
 * T: The processed data type (QuoteData, string[], etc.)
 *
 * This hook encapsulates:
 * - Data fetching logic
 * - Error handling
 * - Loading states
 * - Data processing through generics
 */
export const useWidgetData = <T>(
  feedType: string,
  processor: (feedData: FeedQueryResult) => T[],
  dynamicFeed?: boolean,
  trending?: boolean
): UseWidgetDataResult<T> => {

  const { i18n } = useTranslation();
  const language = i18n.language;

  const {
    data: feedData,
    isLoading: loading,
    isError,
    error,
    refetch,
  } = useQuery<FeedQueryResult, Error, FeedQueryResult>({
    queryKey: dynamicFeed ? ['feed', feedType, language] : [language, feedType],
    queryFn: async () => {
      const fetchQuery = generateFetchFeedQuery(feedType)({
        lang: language,
        trending: trending || false
      });
      return await fetchQuery();
    },
    enabled: !!feedType,
  });

  //Memoized data processing - this is where the "inheritance" magic happens
  const processedData = useMemo(() => {
    return feedData ? processor(feedData) : [];
  }, [feedData, processor]);

  const errorMessage = isError
    ? (error instanceof Error ? error.message : `Failed to fetch ${feedType} data`)
    : '';

  const refetchCallback = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    data: processedData,
    loading,
    error: errorMessage,
    refetch: refetchCallback
  };
};
